import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { getVirusTotalAnalysis } from '@/lib/api/virustotal';
import { AlertTriangle, CheckCircle, AlertCircle, Clock, Vote, FlaskConical, ShieldAlert } from 'lucide-react';

// ... (keep existing helper functions: getDetectionColor, getDetectionProgressColor, formatTimestamp, getSignedStatus, InfoItem, getSigmaSeverityColor) ...
function getDetectionColor(rate: number): string {
  if (rate <= 20) return 'text-green-500 dark:text-green-400';
  if (rate <= 40) return 'text-yellow-500 dark:text-yellow-400';
  if (rate <= 60) return 'text-orange-500 dark:text-orange-400';
  if (rate <= 80) return 'text-red-400 dark:text-red-300';
  return 'text-red-500 dark:text-red-400';
}

function getDetectionProgressColor(rate: number): string {
  if (rate <= 20) return 'bg-green-500';
  if (rate <= 40) return 'bg-yellow-500';
  if (rate <= 60) return 'bg-orange-500';
  if (rate <= 80) return 'bg-red-400';
  return 'bg-red-500';
}

function formatTimestamp(timestamp: number | undefined | null): string {
  if (typeof timestamp !== 'number' || timestamp <= 0) return 'N/A';
  try {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      hour12: false,
    };
    return new Date(timestamp * 1000).toLocaleString(undefined, options);
  } catch (e) {
    console.error("Error formatting timestamp:", timestamp, e);
    return 'Invalid Date';
  }
}

function getSignedStatus(analysisData: any): boolean | undefined {
  try {
    const signedValue = analysisData?.data?.attributes?.sigma_analysis_results?.[0]?.match_context?.[0]?.values?.Signed;
    if (typeof signedValue === 'boolean') return signedValue;
    if (typeof signedValue === 'string') {
      const lowerCaseValue = signedValue.trim().toLowerCase();
      if (lowerCaseValue === 'true') return true;
      if (lowerCaseValue === 'false') return false;
    }
  } catch (e) {
    console.error("Error accessing Signed status:", e);
  }
  return undefined;
}

function InfoItem({ label, value }: { label: string; value: React.ReactNode | string | number | undefined | null }) {
    if (value === undefined || value === null || value === '') return null;
    // Add break-inside-avoid to prevent items from breaking across columns in some scenarios
    return (
        <div className="break-inside-avoid mb-3 md:mb-2">
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="font-medium break-words">{typeof value === 'string' || typeof value === 'number' ? value : value}</div>
        </div>
    );
}

function getSigmaSeverityColor(severity: string): string {
    const lowerSeverity = severity.toLowerCase();
    if (lowerSeverity === 'critical' || lowerSeverity === 'high') {
        return 'text-destructive font-semibold';
    }
    if (lowerSeverity === 'medium' || lowerSeverity === 'low') {
        return 'text-yellow-500 dark:text-yellow-400 font-semibold';
    }
    return '';
}

export function VirusTotalAnalysis({ hash }: { hash: string }) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // ... (fetchData logic remains the same) ...
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      setData(null);
      try {
        const response = await getVirusTotalAnalysis(hash);
        setData(response);
      } catch (err) {
        setError('Failed to fetch VirusTotal analysis');
        console.error(`Error fetching VT data for hash ${hash}:`, err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [hash]);


  if (isLoading) {
     // ... (loading state remains the same) ...
    return (
      <Card>
        <CardHeader> <CardTitle>VirusTotal Analysis</CardTitle> </CardHeader>
        <CardContent className="h-[400px] flex items-center justify-center"> <div className="text-muted-foreground">Loading analysis for {hash}...</div> </CardContent>
      </Card>
    );
  }

  if (error || !data?.data?.attributes) {
     // ... (error state remains the same) ...
    return (
      <Card>
        <CardHeader> <CardTitle>VirusTotal Analysis</CardTitle> </CardHeader>
        <CardContent className="h-[400px] flex items-center justify-center"> <div className="text-destructive flex items-center gap-2"> <AlertTriangle className="h-5 w-5" /> {error || 'Failed to load VirusTotal analysis or data structure is invalid'} </div> </CardContent>
      </Card>
    );
  }

  // --- Data Extraction and Conditional Logic (remains the same) ---
  const attributes = data.data.attributes;
  const stats = attributes.last_analysis_stats || {};
  const signatureInfo = attributes.signature_info;
  const totalVotes = attributes.total_votes;
  const sandboxVerdicts = attributes.sandbox_verdicts;
  const sigmaSummary = attributes.sigma_analysis_summary;

  const totalScans = Object.values(stats).reduce((a: any, b: any) => (typeof a === 'number' ? a : 0) + (typeof b === 'number' ? b : 0), 0) || 1;
  const maliciousCount = stats.malicious ?? 0;
  const suspiciousCount = stats.suspicious ?? 0;
  const undetectedCount = stats.undetected ?? 0;
  const timeoutCount = stats.timeout ?? 0;
  const unsupportedCount = stats['type-unsupported'] ?? 0;
  const detectionRate = typeof totalScans === 'number' && totalScans > 0 ? (((maliciousCount ?? 0) + (suspiciousCount ?? 0)) / totalScans) * 100 : 0;

  const lastAnalysisResults = attributes.last_analysis_results || {};
  const results = Object.entries(lastAnalysisResults).map(([name, result]: [string, any]) => ({ name, ...(typeof result === 'object' && result !== null ? result : {}) })).sort((a, b) => {
    if (a.result && !b.result) return -1; if (!a.result && b.result) return 1;
    const categoryOrder = { malicious: 0, suspicious: 1, timeout: 2, 'type-unsupported': 3, undetected: 4 };
    const aOrder = categoryOrder[a.category as keyof typeof categoryOrder] ?? 5; const bOrder = categoryOrder[b.category as keyof typeof categoryOrder] ?? 5;
    if (aOrder !== bOrder) return aOrder - bOrder; return a.name.localeCompare(b.name);
  });

  const signedStatus = getSignedStatus(data);
  const firstSubmissionDate = formatTimestamp(attributes.first_submission_date);
  const creationDate = formatTimestamp(attributes.creation_date);
  const lastModificationDate = formatTimestamp(attributes.last_modification_date);
  const fileSize = typeof attributes.size === 'number' ? attributes.size.toLocaleString() + ' bytes' : 'N/A';
  const fileTypeDescription = attributes.type_description || 'N/A';
  const magic = attributes.magic || 'N/A';
  const reputation = typeof attributes.reputation === 'number' ? attributes.reputation : 'N/A';
  const meaningfulName = attributes.meaningful_name || 'N/A';
  const lastAnalysisDate = formatTimestamp(attributes.last_analysis_date);
  const lastSubmissionDate = formatTimestamp(attributes.last_submission_date);
  const firstSeenITWDate = formatTimestamp(attributes.first_seen_itw_date);

  const showVotesCard = totalVotes && ((totalVotes.malicious ?? 0) > 0 || (totalVotes.harmless ?? 0) > 0);
  let showSandboxCard = false;
  if (sandboxVerdicts && Object.keys(sandboxVerdicts).length > 0) {
    const verdictEntries = Object.entries(sandboxVerdicts);
    if (verdictEntries.length > 1 || (verdictEntries.length === 1 && (verdictEntries[0][1] as any)?.category !== 'undetected')) { showSandboxCard = true; }
  }
  const showSigmaCard = sigmaSummary && Object.values(sigmaSummary).some(summary => ((summary as any).critical ?? 0) > 0 || ((summary as any).high ?? 0) > 0 || ((summary as any).medium ?? 0) > 0 || ((summary as any).low ?? 0) > 0);


  return (
    <Card>
      <CardHeader> <CardTitle>VirusTotal Analysis</CardTitle> </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {signatureInfo && Object.keys(signatureInfo).length > 0 && ( <TabsTrigger value="signature">Signature Information</TabsTrigger> )}
            <TabsTrigger value="detections">Detections</TabsTrigger>
          </TabsList>

          {/* --- Overview Tab --- */}
          <TabsContent value="overview" className="space-y-6 mt-4">
            {/* Stats Cards (remain the same) */}
             <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
               <Card> <CardContent className="pt-6"> <div className="text-center"> <div className="flex justify-center mb-2"> <AlertTriangle className="h-8 w-8 text-destructive" /> </div> <div className="text-2xl font-bold">{maliciousCount}</div> <p className="text-sm text-muted-foreground">Malicious</p> </div> </CardContent> </Card>
               <Card> <CardContent className="pt-6"> <div className="text-center"> <div className="flex justify-center mb-2"> <AlertCircle className="h-8 w-8 text-yellow-500" /> </div> <div className="text-2xl font-bold">{suspiciousCount}</div> <p className="text-sm text-muted-foreground">Suspicious</p> </div> </CardContent> </Card>
               <Card> <CardContent className="pt-6"> <div className="text-center"> <div className="flex justify-center mb-2"> <CheckCircle className="h-8 w-8 text-green-500" /> </div> <div className="text-2xl font-bold">{undetectedCount}</div> <p className="text-sm text-muted-foreground">Clean</p> </div> </CardContent> </Card>
               <Card> <CardContent className="pt-6"> <div className="text-center"> <div className="flex justify-center mb-2"> <Clock className="h-8 w-8 text-muted-foreground" /> </div> <div className="text-2xl font-bold">{timeoutCount + unsupportedCount}</div> <p className="text-sm text-muted-foreground">Timeout/Unsupported</p> </div> </CardContent> </Card>
             </div>

            {/* Detection Rate (remains the same) */}
             <Card> <CardContent className="pt-6"> <div className="space-y-2"> <div className="flex items-center justify-between text-sm"> <span>Detection Rate</span> <span className={getDetectionColor(detectionRate)}>{detectionRate.toFixed(1)}%</span> </div> <Progress value={detectionRate} className="h-2" indicatorClassName={getDetectionProgressColor(detectionRate)} /> </div> </CardContent> </Card>

            {/* --- UPDATED: File Information --- */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">File Information</h3>
              {/* Keep the outer grid for the two cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {/* Left Card: Timestamps */}
                 <Card>
                    {/* Apply grid layout inside CardContent */}
                    <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0">
                        {/* Timestamps will flow into two columns */}
                        <InfoItem label="First Seen ITW" value={firstSeenITWDate} />
                        <InfoItem label="First Submission" value={firstSubmissionDate} />
                        <InfoItem label="Last Submission" value={lastSubmissionDate} />
                        <InfoItem label="Last Analysis" value={lastAnalysisDate} />
                        <InfoItem label="Creation Date" value={creationDate} />
                        <InfoItem label="Last Modified" value={lastModificationDate} />
                    </CardContent>
                 </Card>

                 {/* Right Card: Details */}
                 <Card>
                    {/* Apply grid layout inside CardContent */}
                    <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0">
                        {/* Details will flow into two columns */}
                        <InfoItem label="Meaningful Name" value={meaningfulName} />
                        <InfoItem label="Size" value={fileSize} />
                        <InfoItem label="Type" value={fileTypeDescription} />
                        <InfoItem label="Magic" value={magic} />
                        {/* Signed Status needs its own div to fit in the grid */}
                        <div className="break-inside-avoid mb-3 md:mb-2">
                            <div className="text-sm text-muted-foreground">Signed Status</div>
                            <div className="font-medium">
                                {signedStatus === true && <span>Yes</span>}
                                {signedStatus === false && <span className="text-destructive font-semibold">Not Signed</span>}
                                {typeof signedStatus !== 'boolean' && <span>N/A</span>}
                            </div>
                        </div>
                    </CardContent>
                 </Card>
              </div>
            </div>
            {/* --- END OF UPDATED FILE INFORMATION --- */}

            {/* Analysis & Behavior Context Cards (logic remains the same) */}
            { (showVotesCard || showSandboxCard || showSigmaCard) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {showVotesCard && ( <Card> <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"> <CardTitle className="text-sm font-medium">Community Votes</CardTitle> <Vote className="h-4 w-4 text-muted-foreground" /> </CardHeader> <CardContent> <div className="text-base"> <span className="text-destructive">{totalVotes.malicious ?? 0}</span> Malicious / <span className="text-green-600">{totalVotes.harmless ?? 0}</span> Harmless </div> </CardContent> </Card> )}
                    {showSandboxCard && ( <Card> <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"> <CardTitle className="text-sm font-medium">Sandbox Verdicts</CardTitle> <FlaskConical className="h-4 w-4 text-muted-foreground" /> </CardHeader> <CardContent> <div className="space-y-1 text-sm"> {Object.entries(sandboxVerdicts).map(([name, verdict]: [string, any]) => ( <div key={name}> <span className="font-semibold">{verdict?.sandbox_name || name}:</span>{' '} <span className={verdict?.category === 'malicious' ? 'destructive' : verdict?.category === 'suspicious' ? 'warning' : 'default'}> {verdict?.category || 'N/A'} </span> </div> ))} </div> </CardContent> </Card> )}
                    {showSigmaCard && ( <Card> <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"> <CardTitle className="text-sm font-medium">Sigma Rule Matches</CardTitle> <ShieldAlert className="h-4 w-4 text-muted-foreground" /> </CardHeader> <CardContent> {Object.entries(sigmaSummary).map(([source, summary]: [string, any]) => { const hitsElements = [ { severity: 'Critical', count: summary.critical ?? 0 }, { severity: 'High', count: summary.high ?? 0 }, { severity: 'Medium', count: summary.medium ?? 0 }, { severity: 'Low', count: summary.low ?? 0 }, ].filter(item => item.count > 0) .map((item, index, arr) => ( <span key={item.severity} className={getSigmaSeverityColor(item.severity)}> {item.count} {item.severity} {index < arr.length - 1 ? ', ' : ''} </span> )); return ( <div key={source} className="text-sm"> <div> {hitsElements.length > 0 ? hitsElements : 'No hits'} </div> </div> ); })} </CardContent> </Card> )}
                </div>
            )}

            {/* Reputation & Tags (remain the same) */}
             <div className="space-y-4">
               <div className="flex items-center gap-3"> <h3 className="text-lg font-semibold">Reputation Score:</h3> {typeof reputation === 'number' ? ( <Badge variant={reputation < 0 ? 'destructive' : 'default'}>{reputation}</Badge> ) : ( <Badge variant="secondary">{reputation}</Badge> )} </div>
               {attributes.tags && attributes.tags.length > 0 && ( <div className="space-y-2"> <div className="text-sm text-muted-foreground">Behavioral Tags</div> <div className="flex flex-wrap gap-2"> {attributes.tags.map((tag: string) => ( <Badge key={tag} variant="secondary">{tag}</Badge> ))} </div> </div> )}
             </div>

            {/* Threat Classification (remain the same) */}
             {attributes.popular_threat_classification && ( <div className="space-y-4"> <h3 className="text-lg font-semibold">Threat Classification</h3> <div className="space-y-2"> {attributes.popular_threat_classification.suggested_threat_label && ( <div className="flex items-center gap-2"> <Badge variant="destructive">{attributes.popular_threat_classification.suggested_threat_label}</Badge> </div> )} {attributes.popular_threat_classification.popular_threat_category?.length > 0 && ( <div className="space-y-1"> <div className="text-sm text-muted-foreground">Categories:</div> <div className="flex flex-wrap gap-2"> {attributes.popular_threat_classification.popular_threat_category.map((category: any) => ( <Badge key={category.value} variant="secondary">{category.value} ({category.count})</Badge> ))} </div> </div> )} {attributes.popular_threat_classification.popular_threat_name?.length > 0 && ( <div className="space-y-1 mt-4"> <div className="text-sm text-muted-foreground">Common Names:</div> <div className="flex flex-wrap gap-2"> {attributes.popular_threat_classification.popular_threat_name.map((name: any) => ( <Badge key={name.value} variant="outline">{name.value} ({name.count})</Badge> ))} </div> </div> )} </div> </div> )}
          </TabsContent>

          {/* --- Signature Info Tab (remains the same) --- */}
          {signatureInfo && Object.keys(signatureInfo).length > 0 && ( <TabsContent value="signature" className="mt-4"> <Card> <CardHeader> <CardTitle>Signature Information</CardTitle> </CardHeader> <CardContent className="space-y-4"> <InfoItem label="Product" value={signatureInfo.product} /> <InfoItem label="Description" value={signatureInfo.description} /> <InfoItem label="Original Filename" value={signatureInfo['original name']} /> <InfoItem label="Internal Name" value={signatureInfo['internal name']} /> <InfoItem label="File Version" value={signatureInfo['file version']} /> <InfoItem label="Copyright" value={signatureInfo.copyright} /> </CardContent> </Card> </TabsContent> )}

          {/* --- Detections Tab (remains the same) --- */}
          <TabsContent value="detections" className="mt-4"> <ScrollArea className="h-[400px] pr-4"> <div className="space-y-2"> {results.length > 0 ? ( results.map((result) => ( <div key={result.name} className="flex items-center justify-between p-2 rounded-lg border"> <div> <div className="font-medium">{result.name || 'Unknown Engine'}</div> <div className="text-sm text-muted-foreground"> {result.engine_version ? `${result.engine_version} (${result.engine_update || 'N/A'})` : 'N/A'} </div> </div> <Badge variant={ result.category === 'malicious' ? 'destructive' : result.category === 'suspicious' ? 'warning' : 'secondary' }> {result.result || result.category || 'Unknown'} </Badge> </div> )) ) : ( <div className="text-muted-foreground text-center p-4">No detection results available.</div> )} </div> </ScrollArea> </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}